import React, { useEffect, useState, useCallback } from "react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Input, Button } from "../../components/common/Primitives";
import api from "../../lib/api";
import { ShieldCheck, X } from "lucide-react";

// Curated one-tap filters for common audit-log queries. Selecting a chip
// pins the corresponding `action` prefix and shows a small "clear" pill.
// The dropdown / manual filters continue to work in parallel.
const QUICK_FILTERS = [
  { key: "property_access", label: "Property Access", action: "admin.property_access.set", icon: ShieldCheck, hint: "Who granted or revoked CRM property access." },
  { key: "role_change", label: "Role Changes", action: "admin.user.role", icon: null, hint: "Role edits on users." },
  { key: "deal_room", label: "Deal Room Ops", action: "admin.deal_room", icon: null, hint: "Deal-room create / update / seed." },
];

export default function AdminAuditLogs() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ action: "", actor_email: "", target: "", since: "" });
  const [quickKey, setQuickKey] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.action) params.action = filters.action;
      if (filters.actor_email) params.actor_email = filters.actor_email;
      if (filters.target) params.target = filters.target;
      if (filters.since) params.since = new Date(filters.since).toISOString();
      const { data } = await api.get("/admin/audit-logs", { params });
      setRows(data || []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const applyQuick = (q) => {
    setQuickKey(q.key);
    setFilters((f) => ({ ...f, action: q.action }));
  };
  const clearQuick = () => {
    setQuickKey(null);
    setFilters((f) => ({ ...f, action: "" }));
  };

  const exportCsv = () => {
    const header = "at,email,role,action,target,meta\n";
    const lines = rows.map((r) => [
      r.at, r.email || "", r.role || "", r.action, r.target || "",
      JSON.stringify(r.meta || {}).replace(/"/g, "'"),
    ].map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([header + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `venturaz-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <DashboardShell title="Audit Logs" subtitle="ADMIN · TRAIL">
      {/* Quick filter chip row */}
      <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="audit-quick-filters">
        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mr-1">Quick filters</span>
        {QUICK_FILTERS.map((q) => {
          const active = quickKey === q.key;
          const Icon = q.icon;
          return (
            <button
              key={q.key}
              type="button"
              onClick={() => applyQuick(q)}
              title={q.hint}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading transition-colors border ${
                active
                  ? "bg-gold/15 border-gold/60 text-[#5A4400]"
                  : "bg-white/60 border-white/30 text-ink-soft hover:border-gold/40 hover:text-ink"
              }`}
              data-testid={`audit-quick-${q.key}`}
            >
              {Icon && <Icon size={12} />}
              {q.label}
            </button>
          );
        })}
        {quickKey && (
          <button
            type="button"
            onClick={clearQuick}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-mono uppercase tracking-widest text-ink-muted hover:text-rose-700 hover:bg-rose-50 transition-colors"
            data-testid="audit-quick-clear"
          >
            <X size={11} /> Clear
          </button>
        )}
      </div>

      <div className="glass rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="audit-filters">
        <Input label="Action starts with" placeholder="e.g. admin.deal_room" value={filters.action} onChange={(e) => { setQuickKey(null); setFilters({ ...filters, action: e.target.value }); }} testId="audit-filter-action" />
        <Input label="Actor email contains" placeholder="admin@" value={filters.actor_email} onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })} testId="audit-filter-email" />
        <Input label="Target contains" placeholder="dr_ or usr_" value={filters.target} onChange={(e) => setFilters({ ...filters, target: e.target.value })} testId="audit-filter-target" />
        <Input label="Since" type="date" value={filters.since} onChange={(e) => setFilters({ ...filters, since: e.target.value })} testId="audit-filter-since" />
        <div className="flex items-end gap-2">
          <Button onClick={load} className="w-full" data-testid="audit-refresh">Refresh</Button>
          <Button variant="ghost" onClick={exportCsv} className="w-full" data-testid="audit-export">Export CSV</Button>
        </div>
      </div>

      <div className="glass rounded-lg overflow-x-auto" data-testid="audit-log-table">
        <div className="px-4 py-2 text-xs text-ink-muted font-mono flex items-center justify-between">
          <span>{loading ? "Loading\u2026" : `Showing ${rows.length} entries`}</span>
          {quickKey && (
            <span className="text-[10px] uppercase tracking-widest text-[#8A6600]" data-testid="audit-active-quick">
              Filter · {QUICK_FILTERS.find((q) => q.key === quickKey)?.label}
            </span>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-ink-muted">
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-3">At</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Target</th>
              <th className="text-left px-4 py-3">Meta</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.log_id} className="border-b border-white/5" data-testid={`audit-row-${r.log_id}`}>
                <td className="px-4 py-2 text-ink-muted font-mono text-xs whitespace-nowrap">{r.at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2 text-ink-soft font-mono text-xs">{r.email || "-"}</td>
                <td className="px-4 py-2 text-ink-soft font-mono text-xs">{r.role || "-"}</td>
                <td className="px-4 py-2 text-[#8A6600] font-mono text-xs whitespace-nowrap">{r.action}</td>
                <td className="px-4 py-2 text-ink-muted font-mono text-xs">{r.target || "-"}</td>
                <td className="px-4 py-2 text-ink-faint font-mono text-[11px] max-w-md truncate" title={JSON.stringify(r.meta || {})}>
                  {Object.keys(r.meta || {}).length ? JSON.stringify(r.meta) : "-"}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-ink-faint">No audit logs match the filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </DashboardShell>
  );
}
